import { FC, useCallback, useState } from 'react';
import { useField } from 'react-final-form';

import { validateDate } from 'src/utils/validations/validateDate';

import { DatesPicker, DatesPickerProps } from '../DatesPicker/DatesPicker';

export interface FormDatesPickerProps
  extends Omit<
    DatesPickerProps,
    'dates' | 'errors' | 'onAddDate' | 'onRemoveDate'
  > {
  name: string;
  onAddDate?: (value: string) => void;
  onRemoveDate?: (value: string) => void;
  excludedDateErrorMessage?: string;
  validateMinMax?: (
    date: string | undefined,
    minDate?: Date,
    maxDate?: Date,
  ) => string | undefined;
}

export const FormDatesPicker: FC<FormDatesPickerProps> = ({
  name,
  minDate,
  maxDate,
  excludedDates,
  onAddDate,
  onRemoveDate,
  minCount,
  excludedDateErrorMessage,
  validateMinMax = validateDate,
}) => {
  const [errors, setErrors] = useState(new Map<string, string>());

  const validate = useCallback(
    (value: Set<string>) => {
      const nextErrors = new Map<string, string>();

      for (const selectedDate of value) {
        const minMaxError = validateMinMax(selectedDate, minDate, maxDate);
        if (minMaxError) {
          nextErrors.set(selectedDate, minMaxError);
          continue;
        }

        const excludedDateError = excludedDates?.has(selectedDate)
          ? excludedDateErrorMessage
          : undefined;
        if (excludedDateError) nextErrors.set(selectedDate, excludedDateError);
      }
      setErrors(nextErrors);
      if (nextErrors.size) return 'Error';

      return '';
    },
    [excludedDateErrorMessage, excludedDates, maxDate, minDate, validateMinMax],
  );

  const { input } = useField(name, { type: 'string', validate });

  const addDate = useCallback(
    (value: string) => {
      const nextDates = new Set(input.value);
      nextDates.add(value);
      input.onChange(nextDates);
      onAddDate?.(value);
    },
    [input, onAddDate],
  );

  const removeDate = useCallback(
    (value: string) => {
      const nextDates = new Set(input.value);
      nextDates.delete(value);
      input.onChange(nextDates);
      onRemoveDate?.(value);
    },
    [input, onRemoveDate],
  );

  return (
    <DatesPicker
      onAddDate={addDate}
      onRemoveDate={removeDate}
      minDate={minDate}
      maxDate={maxDate}
      dates={input.value}
      errors={errors}
      excludedDates={excludedDates}
      minCount={minCount}
    />
  );
};
